import { useCallback, useEffect, useMemo, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Paper from '@mui/material/Paper';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import InputAdornment from '@mui/material/InputAdornment';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import TableSortLabel from '@mui/material/TableSortLabel';
import { styled } from '@mui/material/styles';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { IdCard as DipendentiIcon, Edit as EditIcon, Delete as DeleteIcon, Search, Mail, Phone, Eye } from 'lucide-react';
import { dipendentiMock } from '../mocks/hr/personaleMockData';
import api from '../services/api';
import { toast } from 'react-toastify';

const emptyForm = {
  utente_id: '',
  tipo_rapporto: '',
  cognome: '',
  nome: '',
  sesso: '',
  data_nascita: '',
  indirizzo_residenza: '',
  email: '',
  pec: '',
  telefono: '',
  cellulare_privato: '',
  cellulare_aziendale: '',
  codice_fiscale: '',
  partita_iva: '',
  iban: '',
  cloudtalk_number: '',
  cloudtalk_interno: ''
};

const TIPO_RAPPORTO_OPTIONS = [
  { value: 'collaboratore', label: 'Collaboratore' },
  { value: 'professionista', label: 'Professionista' },
  { value: 'dipendente_ti', label: 'Dipendente a T.I.' },
  { value: 'dipendente_td', label: 'Dipendente a T.D.' },
  { value: 'stagista', label: 'Stagista' },
  { value: 'apprendistato', label: 'Apprendistato' },
  { value: 'tirocinio', label: 'Tirocinio' }
];

const tipoRapportoLabel = (value, labelFromApi) =>
  labelFromApi || TIPO_RAPPORTO_OPTIONS.find((opt) => opt.value === value)?.label || '-';

const sessoLabel = (value) => {
  if (value === 'M') return 'Maschio';
  if (value === 'F') return 'Femmina';
  if (value === 'Altro') return 'Altro';
  return '-';
};

const StyledCard = styled(Card)(() => ({
  boxShadow:
    '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const StatWidget = ({ title, value, icon: Icon, color }) => (
  <Paper
    sx={{
      p: 2,
      display: 'flex',
      alignItems: 'center',
      height: '100%',
      borderWidth: 1,
      borderStyle: 'solid',
      borderColor: 'rgba(145, 158, 171, 0.24)',
      boxShadow:
        '0 0 2px 0 rgba(145, 158, 171, 0.16), 0 12px 24px -4px rgba(145, 158, 171, 0.08)'
    }}
    elevation={0}
    variant="outlined"
  >
    <Box
      sx={{
        p: 1.5,
        borderRadius: '50%',
        bgcolor: `${color}15`,
        color,
        mr: 2,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}
    >
      <Icon size={22} />
    </Box>
    <Box>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
        {title}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 'bold' }}>
        {value}
      </Typography>
    </Box>
  </Paper>
);

export default function HrDipendenti() {
  const [ricerca, setRicerca] = useState('');
  const [sourceMode, setSourceMode] = useState('api');
  const [loadingList, setLoadingList] = useState(true);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [summaryApi, setSummaryApi] = useState(null);
  const [elenco, setElenco] = useState([]);
  const [dipendenteSelezionato, setDipendenteSelezionato] = useState(null);
  const [schedaApi, setSchedaApi] = useState(null);
  const [tabDettaglio, setTabDettaglio] = useState('anagrafica');
  const [schedaDialogOpen, setSchedaDialogOpen] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [filtroTipoRapporto, setFiltroTipoRapporto] = useState('all');
  const [filtroCollegamento, setFiltroCollegamento] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [sortBy, setSortBy] = useState('cognome');
  const [sortDir, setSortDir] = useState('ASC');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState('create');
  const [dialogSaving, setDialogSaving] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [formData, setFormData] = useState(emptyForm);
  const [utentiOptions, setUtentiOptions] = useState([]);
  const [loadingUtenti, setLoadingUtenti] = useState(false);

  const mapRowToListItem = (row) => ({
    id: row.id,
    utente_id: row.utente_id ?? null,
    tipo_rapporto: row.tipo_rapporto || '-',
    tipo_rapporto_label: row.tipo_rapporto_label || null,
    cognome: row.cognome || '',
    nome: row.nome || '',
    nominativo: `${row.cognome || ''} ${row.nome || ''}`.trim(),
    email: row.email || '-',
    telefono: row.telefono || '-',
    codice_fiscale: row.codice_fiscale || '-',
    partita_iva: row.partita_iva || '-'
  });

  const loadDetail = async (id) => {
    try {
      setLoadingDetail(true);
      const res = await api.get(`/dipendenti/${id}`);
      setSchedaApi(res.data || null);
    } catch {
      setSchedaApi(null);
    } finally {
      setLoadingDetail(false);
    }
  };

  const openSchedaDialogByRow = async (row) => {
    setDipendenteSelezionato(row);
    setTabDettaglio('anagrafica');
    setSchedaDialogOpen(true);
    if (sourceMode === 'api' && row?.id) {
      await loadDetail(row.id);
    }
  };

  const loadList = async (preferredId = null) => {
    try {
      setLoadingList(true);
      const res = await api.get('/dipendenti', {
        params: {
          q: ricerca.trim(),
          limit: rowsPerPage,
          offset: page * rowsPerPage,
          sortBy,
          sortDir,
          tipoRapporto: filtroTipoRapporto,
          collegamento: filtroCollegamento
        }
      });
      const rows = res.data?.items || [];
      const mapped = rows.map(mapRowToListItem);
      setElenco(mapped);
      setTotalCount(Number(res.data?.total || 0));
      setSourceMode('api');

      let selected = preferredId ? mapped.find((item) => item.id === preferredId) : null;
      if (!selected) selected = mapped[0] || null;

      setDipendenteSelezionato(selected);
      if (selected?.id) loadDetail(selected.id);
      else setSchedaApi(null);
    } catch {
      setSourceMode('mock');
      setTotalCount(0);
      const fallback = (dipendentiMock.elenco || []).map((row, index) => ({
        id: index + 1,
        utente_id: null,
        cognome: (row.nominativo || '').split(' ').slice(1).join(' ') || '-',
        nome: (row.nominativo || '').split(' ')[0] || '-',
        nominativo: row.nominativo || '-',
        email: '-',
        telefono: '-',
        codice_fiscale: '-',
        partita_iva: '-'
      }));
      setElenco(fallback);
      setDipendenteSelezionato(fallback[0] || null);
      setSchedaApi(null);
    } finally {
      setLoadingList(false);
    }
  };

  const loadSummary = useCallback(async () => {
    if (sourceMode !== 'api') {
      setSummaryApi(null);
      return;
    }
    try {
      const res = await api.get('/dipendenti/summary', {
        params: {
          q: ricerca.trim(),
          tipoRapporto: filtroTipoRapporto,
          collegamento: filtroCollegamento
        }
      });
      setSummaryApi(res.data || null);
    } catch (error) {
      setSummaryApi(null);
    }
  }, [filtroCollegamento, filtroTipoRapporto, ricerca, sourceMode]);

  useEffect(() => {
    loadList();
  }, [page, rowsPerPage, sortBy, sortDir, ricerca, filtroTipoRapporto, filtroCollegamento]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    setPage(0);
  }, [ricerca, filtroTipoRapporto, filtroCollegamento, rowsPerPage, sortBy, sortDir]);

  const loadUtentiOptions = async () => {
    if (sourceMode !== 'api') return;
    try {
      setLoadingUtenti(true);
      const res = await api.get('/users', { params: { page: 1, limit: 500, sort: 'name', order: 'ASC' } });
      setUtentiOptions(res.data?.users || []);
    } catch (error) {
      console.error('Errore caricamento utenti collegabili:', error);
      toast.warn('Impossibile caricare la lista utenti');
      setUtentiOptions([]);
    } finally {
      setLoadingUtenti(false);
    }
  };

  const openCreateDialog = () => {
    if (sourceMode !== 'api') {
      toast.warn('Creazione disponibile solo con API attiva');
      return;
    }
    setDialogMode('create');
    setFormData(emptyForm);
    loadUtentiOptions();
    setDialogOpen(true);
  };

  const openEditDialog = async () => {
    if (sourceMode !== 'api') {
      toast.warn('Modifica disponibile solo con API attiva');
      return;
    }
    if (!dipendenteSelezionato?.id) {
      toast.info('Seleziona un dipendente da modificare');
      return;
    }

    try {
      const res = await api.get(`/dipendenti/${dipendenteSelezionato.id}`);
      const d = res.data || {};
      setDialogMode('edit');
      setFormData({
        utente_id: d.utente_id ?? '',
        tipo_rapporto: d.tipo_rapporto || '',
        cognome: d.cognome || '',
        nome: d.nome || '',
        sesso: d.sesso || '',
        data_nascita: d.data_nascita ? String(d.data_nascita).slice(0, 10) : '',
        indirizzo_residenza: d.indirizzo_residenza || '',
        email: d.email || '',
        pec: d.pec || '',
        telefono: d.telefono || '',
        cellulare_privato: d.cellulare_privato || '',
        cellulare_aziendale: d.cellulare_aziendale || '',
        codice_fiscale: d.codice_fiscale || '',
        partita_iva: d.partita_iva || '',
        iban: d.iban || '',
        cloudtalk_number: d.cloudtalk_number || '',
        cloudtalk_interno: d.cloudtalk_interno || ''
      });
      loadUtentiOptions();
      setDialogOpen(true);
    } catch (error) {
      console.error('Errore nel caricamento dettaglio dipendente:', error);
      toast.error('Errore nel caricamento del dipendente');
    }
  };

  const openEditDialogByRow = async (row) => {
    if (sourceMode !== 'api') {
      toast.warn('Modifica disponibile solo con API attiva');
      return;
    }
    if (!row?.id) {
      toast.info('Dipendente non valido');
      return;
    }

    setDipendenteSelezionato(row);
    try {
      const res = await api.get(`/dipendenti/${row.id}`);
      const d = res.data || {};
      setDialogMode('edit');
      setFormData({
        utente_id: d.utente_id ?? '',
        tipo_rapporto: d.tipo_rapporto || '',
        cognome: d.cognome || '',
        nome: d.nome || '',
        sesso: d.sesso || '',
        data_nascita: d.data_nascita ? String(d.data_nascita).slice(0, 10) : '',
        indirizzo_residenza: d.indirizzo_residenza || '',
        email: d.email || '',
        pec: d.pec || '',
        telefono: d.telefono || '',
        cellulare_privato: d.cellulare_privato || '',
        cellulare_aziendale: d.cellulare_aziendale || '',
        codice_fiscale: d.codice_fiscale || '',
        partita_iva: d.partita_iva || '',
        iban: d.iban || '',
        cloudtalk_number: d.cloudtalk_number || '',
        cloudtalk_interno: d.cloudtalk_interno || ''
      });
      loadUtentiOptions();
      setDialogOpen(true);
    } catch (error) {
      console.error('Errore nel caricamento dettaglio dipendente:', error);
      toast.error('Errore nel caricamento del dipendente');
    }
  };

  const handleSaveDipendente = async () => {
    if (!formData.tipo_rapporto || !formData.cognome.trim() || !formData.nome.trim()) {
      toast.error('Tipo rapporto, cognome e nome sono obbligatori');
      return;
    }

    const payload = {
      utente_id: formData.utente_id === '' ? null : Number(formData.utente_id),
      tipo_rapporto: formData.tipo_rapporto,
      cognome: formData.cognome.trim(),
      nome: formData.nome.trim(),
      sesso: formData.sesso || null,
      data_nascita: formData.data_nascita || null,
      indirizzo_residenza: formData.indirizzo_residenza || null,
      email: formData.email || null,
      pec: formData.pec || null,
      telefono: formData.telefono || null,
      cellulare_privato: formData.cellulare_privato || null,
      cellulare_aziendale: formData.cellulare_aziendale || null,
      codice_fiscale: formData.codice_fiscale.trim() ? formData.codice_fiscale.trim().toUpperCase() : null,
      partita_iva: formData.partita_iva || null,
      iban: formData.iban || null,
      cloudtalk_number: formData.cloudtalk_number || null,
      cloudtalk_interno: formData.cloudtalk_interno || null
    };

    try {
      setDialogSaving(true);
      if (dialogMode === 'create') {
        const res = await api.post('/dipendenti', payload);
        toast.success('Dipendente creato');
        setDialogOpen(false);
        await loadList(res.data?.id || null);
      } else {
        await api.put(`/dipendenti/${dipendenteSelezionato.id}`, payload);
        toast.success('Dipendente aggiornato');
        setDialogOpen(false);
        await loadList(dipendenteSelezionato.id);
      }
    } catch (error) {
      console.error('Errore salvataggio dipendente:', error);
      toast.error(error.response?.data?.message || 'Errore nel salvataggio del dipendente');
    } finally {
      setDialogSaving(false);
    }
  };

  const handleDeleteDipendenteByRow = (row) => {
    if (sourceMode !== 'api') {
      toast.warn('Eliminazione disponibile solo con API attiva');
      return;
    }
    if (!row?.id) {
      toast.info('Dipendente non valido');
      return;
    }

    setDeleteTarget(row);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDeleteDipendente = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/dipendenti/${deleteTarget.id}`);
      toast.success('Dipendente eliminato');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await loadList();
    } catch (error) {
      console.error('Errore eliminazione dipendente:', error);
      toast.error(error.response?.data?.message || 'Errore nell\'eliminazione del dipendente');
    } finally {
      setDeleteLoading(false);
    }
  };

  const elencoFiltrato = useMemo(() => {
    const term = ricerca.trim().toLowerCase();
    return elenco.filter((row) => {
      const matchesSearch = term
        ? `${row.cognome} ${row.nome} ${row.email} ${row.codice_fiscale} ${row.partita_iva}`.toLowerCase().includes(term)
        : true;
      const matchesTipo = filtroTipoRapporto === 'all' ? true : row.tipo_rapporto === filtroTipoRapporto;
      const matchesCollegamento =
        filtroCollegamento === 'all'
          ? true
          : filtroCollegamento === 'collegato'
            ? Boolean(row.utente_id)
            : !row.utente_id;
      return matchesSearch && matchesTipo && matchesCollegamento;
    });
  }, [elenco, ricerca, filtroTipoRapporto, filtroCollegamento]);

  const pagedRows = useMemo(
    () => elencoFiltrato.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [elencoFiltrato, page, rowsPerPage]
  );
  const displayedRows = sourceMode === 'api' ? elenco : pagedRows;

  const summary = useMemo(() => {
    const totaleFiltrato = sourceMode === 'api' ? totalCount : elencoFiltrato.length;
    const totale = sourceMode === 'api' ? Number(summaryApi?.totale || 0) : elenco.length;
    const collegati = sourceMode === 'api' ? Number(summaryApi?.collegati || 0) : elenco.filter((row) => Boolean(row.utente_id)).length;
    const nonCollegati = sourceMode === 'api' ? Number(summaryApi?.nonCollegati || 0) : (totale - collegati);
    const conEmail = sourceMode === 'api' ? Number(summaryApi?.conEmail || 0) : elenco.filter((row) => row.email && row.email !== '-').length;
    return {
      totaleFiltrato,
      totale,
      collegati,
      nonCollegati,
      conEmail
    };
  }, [elenco, elencoFiltrato, sourceMode, summaryApi, totalCount]);

  const handleRequestSort = (field) => {
    if (sortBy === field) {
      setSortDir((prev) => (prev === 'ASC' ? 'DESC' : 'ASC'));
      return;
    }
    setSortBy(field);
    setSortDir('ASC');
  };

  const utenteIdsOccupati = useMemo(
    () => new Set(elenco.map((item) => item.utente_id).filter((value) => value !== null && value !== undefined)),
    [elenco]
  );

  const utentiCollegabili = useMemo(() => {
    const currentLinked = formData.utente_id === '' ? null : Number(formData.utente_id);
    return utentiOptions.filter((user) => !utenteIdsOccupati.has(user.id) || currentLinked === user.id);
  }, [formData.utente_id, utentiOptions, utenteIdsOccupati]);

  const utenteSelezionatoOption = useMemo(() => {
    if (formData.utente_id === '' || formData.utente_id === null) return null;
    const id = Number(formData.utente_id);
    return utentiCollegabili.find((user) => user.id === id) || null;
  }, [formData.utente_id, utentiCollegabili]);

  const schedaSelezionata = useMemo(() => {
    if (sourceMode !== 'api') {
      if (!dipendenteSelezionato) return null;
      return {
        cognome: dipendenteSelezionato.cognome,
        nome: dipendenteSelezionato.nome,
        tipo_rapporto: dipendenteSelezionato.tipo_rapporto,
        tipo_rapporto_label: dipendenteSelezionato.tipo_rapporto_label || null,
        sesso: '-',
        data_nascita: '-',
        indirizzo_residenza: '-',
        email: dipendenteSelezionato.email,
        pec: '-',
        telefono: dipendenteSelezionato.telefono,
        cellulare_privato: '-',
        cellulare_aziendale: '-',
        codice_fiscale: dipendenteSelezionato.codice_fiscale,
        partita_iva: '-',
        iban: '-',
        cloudtalk_number: '-',
        cloudtalk_interno: '-'
      };
    }
    return schedaApi;
  }, [dipendenteSelezionato, schedaApi, sourceMode]);

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{ width: 40, height: 40, borderRadius: '50%', bgcolor: '#eef2ff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <DipendentiIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Dipendenti/Collaboratori</Typography>
            <Typography variant="body2" color="text.secondary">Anagrafica dipendenti collegata agli utenti applicazione.</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button variant="contained" onClick={openCreateDialog} disabled={sourceMode !== 'api'}>
            + nuovo Dipendente
          </Button>
        </Box>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <StatWidget title="Dipendenti/Collaboratori filtrati" value={summary.totaleFiltrato} icon={DipendentiIcon} color="#2563EB" />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatWidget title="Account collegati (totale)" value={summary.collegati} icon={ViewColumnIcon} color="#16A34A" />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatWidget title="Senza account (totale)" value={summary.nonCollegati} icon={FilterAltIcon} color="#DC2626" />
        </Grid>
        <Grid item xs={12} md={3}>
          <StatWidget title="Email valorizzate (totale)" value={summary.conEmail} icon={Mail} color="#CA8A04" />
        </Grid>
      </Grid>

      <StyledCard sx={{ mb: 2 }}>
        <Box
          sx={{
            px: 3,
            py: 2,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <TextField
              size="small"
              placeholder="Cerca per cognome, nome, email, CF..."
              value={ricerca}
              onChange={(e) => setRicerca(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} />
                  </InputAdornment>
                )
              }}
              sx={{ minWidth: 300 }}
            />
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel>Tipo rapporto</InputLabel>
              <Select label="Tipo rapporto" value={filtroTipoRapporto} onChange={(e) => setFiltroTipoRapporto(e.target.value)}>
                <MenuItem value="all">Tutti</MenuItem>
                {TIPO_RAPPORTO_OPTIONS.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Tooltip title="Mostra/Nascondi filtri">
              <IconButton size="small" onClick={() => setShowFilters((prev) => !prev)}>
                <FilterAltIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Vista tabella">
              <IconButton size="small">
                <ViewColumnIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </Box>

        {showFilters && (
          <Box
            sx={{
              px: 3,
              py: 2,
              borderBottom: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              flexWrap: 'wrap',
              gap: 2
            }}
          >
            <FormControl size="small" sx={{ minWidth: 220 }}>
              <InputLabel>Collegamento account</InputLabel>
              <Select
                label="Collegamento account"
                value={filtroCollegamento}
                onChange={(e) => setFiltroCollegamento(e.target.value)}
              >
                <MenuItem value="all">Tutti</MenuItem>
                <MenuItem value="collegato">Collegati</MenuItem>
                <MenuItem value="non_collegato">Non collegati</MenuItem>
              </Select>
            </FormControl>
          </Box>
        )}

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'background.neutral' }}>
                <TableCell sortDirection={sortBy === 'cognome' ? sortDir.toLowerCase() : false}>
                  <TableSortLabel
                    active={sortBy === 'cognome'}
                    direction={sortBy === 'cognome' ? sortDir.toLowerCase() : 'asc'}
                    onClick={() => handleRequestSort('cognome')}
                  >
                    Dipendente
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'tipo_rapporto' ? sortDir.toLowerCase() : false}>
                  <TableSortLabel
                    active={sortBy === 'tipo_rapporto'}
                    direction={sortBy === 'tipo_rapporto' ? sortDir.toLowerCase() : 'asc'}
                    onClick={() => handleRequestSort('tipo_rapporto')}
                  >
                    Tipo rapporto
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'codice_fiscale' ? sortDir.toLowerCase() : false}>
                  <TableSortLabel
                    active={sortBy === 'codice_fiscale'}
                    direction={sortBy === 'codice_fiscale' ? sortDir.toLowerCase() : 'asc'}
                    onClick={() => handleRequestSort('codice_fiscale')}
                  >
                    Codice fiscale
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortBy === 'partita_iva' ? sortDir.toLowerCase() : false}>
                  <TableSortLabel
                    active={sortBy === 'partita_iva'}
                    direction={sortBy === 'partita_iva' ? sortDir.toLowerCase() : 'asc'}
                    onClick={() => handleRequestSort('partita_iva')}
                  >
                    Partita IVA
                  </TableSortLabel>
                </TableCell>
                <TableCell>Stato account</TableCell>
                <TableCell align="right" sx={{ minWidth: 150, whiteSpace: 'nowrap' }}>
                  Azioni
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loadingList && (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
              {!loadingList &&
                displayedRows.map((row) => (
                  <TableRow
                    key={row.id}
                    hover
                    onClick={() => {
                      setDipendenteSelezionato(row);
                      if (sourceMode === 'api') loadDetail(row.id);
                    }}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: dipendenteSelezionato?.id === row.id ? 'rgba(79, 70, 229, 0.08)' : 'transparent'
                    }}
                  >
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                        {row.cognome} {row.nome}
                      </Typography>
                      <Stack spacing={0.6} sx={{ mt: 0.7 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Mail size={14} color="#64748b" />
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                            {row.email || '-'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                          <Phone size={14} color="#64748b" />
                          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                            {row.telefono || '-'}
                          </Typography>
                        </Box>
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" variant="outlined" color="info" label={tipoRapportoLabel(row.tipo_rapporto, row.tipo_rapporto_label)} />
                    </TableCell>
                    <TableCell>{row.codice_fiscale}</TableCell>
                    <TableCell>{row.partita_iva || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        size="small"
                        label={row.utente_id ? 'Collegato' : 'Non collegato'}
                        color={row.utente_id ? 'success' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="right" sx={{ minWidth: 150, whiteSpace: 'nowrap' }}>
                      <Box
                        sx={{
                          display: 'inline-flex',
                          flexDirection: 'row',
                          alignItems: 'center',
                          justifyContent: 'flex-end',
                          gap: 0.75,
                          flexWrap: 'nowrap'
                        }}
                      >
                        <Tooltip title="Visualizza scheda dipendente">
                          <IconButton
                            size="small"
                            onClick={(event) => {
                              event.stopPropagation();
                              openSchedaDialogByRow(row);
                            }}
                            sx={{
                              width: 32,
                              height: 32,
                              color: '#0ea5e9',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Eye size={17} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Modifica anagrafica">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(event) => {
                              event.stopPropagation();
                              openEditDialogByRow(row);
                            }}
                            sx={{ mr: 1 }}
                          >
                            <EditIcon size={18} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Elimina dipendente">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(event) => {
                              event.stopPropagation();
                              handleDeleteDipendenteByRow(row);
                            }}
                          >
                            <DeleteIcon size={18} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              {!loadingList && displayedRows.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    Nessun dipendente trovato
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          component="div"
          rowsPerPageOptions={[10, 25, 50]}
          count={sourceMode === 'api' ? totalCount : elencoFiltrato.length}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => setRowsPerPage(parseInt(event.target.value, 10))}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (deleteLoading) return;
          setDeleteDialogOpen(false);
          setDeleteTarget(null);
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle sx={{ pb: 1 }}>Conferma eliminazione</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 1.5 }}>
            Stai per eliminare definitivamente questo dipendente:
          </Typography>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {(deleteTarget?.cognome || deleteTarget?.nome)
              ? `${deleteTarget?.cognome || ''} ${deleteTarget?.nome || ''}`.trim()
              : (deleteTarget?.nominativo || 'Dipendente selezionato')}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
            L&apos;operazione non può essere annullata.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteTarget(null);
            }}
            disabled={deleteLoading}
          >
            Annulla
          </Button>
          <Button
            onClick={handleConfirmDeleteDipendente}
            color="error"
            variant="contained"
            disabled={deleteLoading}
          >
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={dialogOpen} onClose={() => (dialogSaving ? null : setDialogOpen(false))} fullWidth maxWidth="md">
        <DialogTitle sx={{ pb: 1 }}>
          {dialogMode === 'create' ? 'Nuovo dipendente' : 'Modifica dipendente'}
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
            Compila i campi obbligatori per salvare l&apos;anagrafica.
          </Typography>
        </DialogTitle>
        <DialogContent dividers sx={{ bgcolor: '#f8fafc' }}>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={4}>
              <Autocomplete
                options={TIPO_RAPPORTO_OPTIONS}
                value={TIPO_RAPPORTO_OPTIONS.find((opt) => opt.value === formData.tipo_rapporto) || null}
                onChange={(_, value) => setFormData((prev) => ({ ...prev, tipo_rapporto: value?.value || '' }))}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => (
                  <TextField {...params} size="small" label="Tipo rapporto" placeholder="Digita per cercare..." required />
                )}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              <Autocomplete
                options={utentiCollegabili}
                value={utenteSelezionatoOption}
                loading={loadingUtenti}
                onChange={(_, value) => {
                  setFormData((prev) => ({ ...prev, utente_id: value ? value.id : '' }));
                }}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                getOptionLabel={(option) => `${option.name || ''} (${option.email || 'senza email'})`}
                noOptionsText="Nessun utente disponibile"
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Utente collegato"
                    placeholder="Cerca per nome o email..."
                  />
                )}
              />
              {loadingUtenti && <Typography variant="caption" color="text.secondary">Caricamento utenti...</Typography>}
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField size="small" label="Cognome" fullWidth required value={formData.cognome} onChange={(e) => setFormData((prev) => ({ ...prev, cognome: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField size="small" label="Nome" fullWidth required value={formData.nome} onChange={(e) => setFormData((prev) => ({ ...prev, nome: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel id="sesso-label">Sesso</InputLabel>
                <Select labelId="sesso-label" label="Sesso" value={formData.sesso} onChange={(e) => setFormData((prev) => ({ ...prev, sesso: e.target.value }))}>
                  <MenuItem value=""><em>Non specificato</em></MenuItem>
                  <MenuItem value="M">Maschio</MenuItem>
                  <MenuItem value="F">Femmina</MenuItem>
                  <MenuItem value="Altro">Altro</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField size="small" label="Data nascita" type="date" fullWidth InputLabelProps={{ shrink: true }} value={formData.data_nascita} onChange={(e) => setFormData((prev) => ({ ...prev, data_nascita: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={8}>
              <TextField size="small" label="Indirizzo residenza" fullWidth value={formData.indirizzo_residenza} onChange={(e) => setFormData((prev) => ({ ...prev, indirizzo_residenza: e.target.value }))} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField size="small" label="Email" type="email" fullWidth value={formData.email} onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField size="small" label="PEC" fullWidth value={formData.pec} onChange={(e) => setFormData((prev) => ({ ...prev, pec: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField size="small" label="Telefono" fullWidth value={formData.telefono} onChange={(e) => setFormData((prev) => ({ ...prev, telefono: e.target.value }))} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField size="small" label="Cellulare privato" fullWidth value={formData.cellulare_privato} onChange={(e) => setFormData((prev) => ({ ...prev, cellulare_privato: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField size="small" label="Cellulare aziendale" fullWidth value={formData.cellulare_aziendale} onChange={(e) => setFormData((prev) => ({ ...prev, cellulare_aziendale: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField size="small" label="Codice fiscale" fullWidth value={formData.codice_fiscale} onChange={(e) => setFormData((prev) => ({ ...prev, codice_fiscale: e.target.value }))} />
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField size="small" label="Partita IVA" fullWidth value={formData.partita_iva} onChange={(e) => setFormData((prev) => ({ ...prev, partita_iva: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField size="small" label="IBAN" fullWidth value={formData.iban} onChange={(e) => setFormData((prev) => ({ ...prev, iban: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField size="small" label="Cloudtalk number" fullWidth value={formData.cloudtalk_number} onChange={(e) => setFormData((prev) => ({ ...prev, cloudtalk_number: e.target.value }))} />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField size="small" label="Cloudtalk interno" fullWidth value={formData.cloudtalk_interno} onChange={(e) => setFormData((prev) => ({ ...prev, cloudtalk_interno: e.target.value }))} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5, borderTop: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Button onClick={() => setDialogOpen(false)} disabled={dialogSaving}>Annulla</Button>
          <Button onClick={handleSaveDipendente} variant="contained" disabled={dialogSaving}>
            {dialogSaving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={schedaDialogOpen}
        onClose={() => setSchedaDialogOpen(false)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle>Scheda dipendente</DialogTitle>
        <DialogContent>
          <Card variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
            {!schedaSelezionata && (
              <Typography variant="body2" color="text.secondary">
                Nessun dipendente selezionato.
              </Typography>
            )}
            {schedaSelezionata && (
              <>
                {loadingDetail && sourceMode === 'api' && (
                  <Box sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="caption" color="text.secondary">Caricamento dettaglio...</Typography>
                  </Box>
                )}
                <Typography variant="h6" sx={{ mb: 1 }}>
                  <strong>{schedaSelezionata.cognome} {schedaSelezionata.nome}</strong>
                </Typography>
                <Tabs value={tabDettaglio} onChange={(_, value) => setTabDettaglio(value)} variant="scrollable" allowScrollButtonsMobile sx={{ minHeight: 36, mb: 1 }}>
                  <Tab value="anagrafica" label="Anagrafica" sx={{ minHeight: 36 }} />
                  <Tab value="contatti" label="Contatti" sx={{ minHeight: 36 }} />
                  <Tab value="fiscale" label="Fiscale" sx={{ minHeight: 36 }} />
                  <Tab value="telefonia" label="Telefonia" sx={{ minHeight: 36 }} />
                </Tabs>

                {tabDettaglio === 'anagrafica' && (
                  <>
                    <Typography variant="body2">Tipo rapporto: {tipoRapportoLabel(schedaSelezionata.tipo_rapporto, schedaSelezionata.tipo_rapporto_label)}</Typography>
                    <Typography variant="body2">Sesso: {sessoLabel(schedaSelezionata.sesso)}</Typography>
                    <Typography variant="body2">Data nascita: {schedaSelezionata.data_nascita ? String(schedaSelezionata.data_nascita).slice(0, 10) : '-'}</Typography>
                    <Typography variant="body2">Indirizzo residenza: {schedaSelezionata.indirizzo_residenza || '-'}</Typography>
                  </>
                )}
                {tabDettaglio === 'contatti' && (
                  <>
                    <Typography variant="body2">Email: {schedaSelezionata.email || '-'}</Typography>
                    <Typography variant="body2">PEC: {schedaSelezionata.pec || '-'}</Typography>
                    <Typography variant="body2">Telefono: {schedaSelezionata.telefono || '-'}</Typography>
                    <Typography variant="body2">Cellulare privato: {schedaSelezionata.cellulare_privato || '-'}</Typography>
                    <Typography variant="body2">Cellulare aziendale: {schedaSelezionata.cellulare_aziendale || '-'}</Typography>
                  </>
                )}
                {tabDettaglio === 'fiscale' && (
                  <>
                    <Typography variant="body2">Codice fiscale: {schedaSelezionata.codice_fiscale || '-'}</Typography>
                    <Typography variant="body2">Partita IVA: {schedaSelezionata.partita_iva || '-'}</Typography>
                    <Typography variant="body2">IBAN: {schedaSelezionata.iban || '-'}</Typography>
                  </>
                )}
                {tabDettaglio === 'telefonia' && (
                  <>
                    <Typography variant="body2">Cloudtalk number: {schedaSelezionata.cloudtalk_number || '-'}</Typography>
                    <Typography variant="body2">Cloudtalk interno: {schedaSelezionata.cloudtalk_interno || '-'}</Typography>
                  </>
                )}
              </>
            )}
          </Card>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSchedaDialogOpen(false)}>Chiudi</Button>
          <Button
            variant="contained"
            onClick={() => {
              setSchedaDialogOpen(false);
              openEditDialog();
            }}
            disabled={sourceMode !== 'api' || !dipendenteSelezionato?.id}
          >
            Modifica
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
